import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { generateCrisisTasks } from '../utils/crisisTaskEngine.js';
import { logHelpfulError, isIndexError } from '../utils/firebaseIndexHelper.js';

/**
 * Smart Task Service - Chunk 3 Implementation
 * Handles intelligent task assignment, workload balancing, and smart task creation
 */

class SmartTaskService {
  constructor(db) {
    this.db = db;
    this.roleCapabilities = {
      cmt_leader: {
        skills: ['leadership', 'coordination', 'decision_making', 'escalation'],
        capacity: 8, // max concurrent tasks
        priority: 1 // highest priority for assignments
      },
      safety_security: {
        skills: ['safety', 'security', 'evacuation', 'threat_assessment'],
        capacity: 6,
        priority: 2
      },
      communications: {
        skills: ['media', 'stakeholder_comms', 'public_relations', 'messaging'],
        capacity: 5,
        priority: 3
      },
      legal: {
        skills: ['legal', 'compliance', 'regulatory', 'contracts'],
        capacity: 4,
        priority: 4
      },
      talent_hr: {
        skills: ['hr', 'employee_relations', 'welfare', 'staffing'],
        capacity: 5,
        priority: 5
      },
      finance: {
        skills: ['finance', 'budgeting', 'insurance', 'cost_analysis'],
        capacity: 4,
        priority: 6
      },
      it_tech: {
        skills: ['technology', 'systems', 'data', 'cybersecurity'],
        capacity: 6,
        priority: 7
      },
      operations: {
        skills: ['operations', 'logistics', 'supply_chain', 'facilities'],
        capacity: 6,
        priority: 8
      },
      external_relations: {
        skills: ['government', 'regulatory', 'external_stakeholders', 'partnerships'],
        capacity: 4,
        priority: 9
      }
    };
  }

  /**
   * Suggest optimal task assignment based on skills, workload, and availability
   */
  async suggestTaskAssignment(task, incidentId, orgId) {
    try {
      // Get current workload for all roles
      const currentWorkload = await this.getCurrentWorkload(incidentId, orgId);
      
      // Analyze task requirements
      const taskRequirements = this.analyzeTaskRequirements(task);
      
      // Score each role for this task
      const roleScores = this.scoreRolesForTask(taskRequirements, currentWorkload);
      
      // Get the best assignment
      const bestAssignment = this.getBestAssignment(roleScores);
      
      return {
        recommendedRole: bestAssignment.role,
        confidence: bestAssignment.score,
        reasoning: bestAssignment.reasoning,
        alternatives: roleScores.slice(1, 3), // Top 2 alternatives
        workloadImpact: this.calculateWorkloadImpact(bestAssignment.role, currentWorkload)
      };
      
    } catch (error) {
      console.error('Error suggesting task assignment:', error);
      return {
        recommendedRole: 'cmt_leader', // fallback
        confidence: 0.5,
        reasoning: 'Default assignment due to analysis error',
        alternatives: [],
        workloadImpact: 'unknown'
      };
    }
  }

  /**
   * Get current workload for all CMT roles
   */
  async getCurrentWorkload(incidentId, orgId) {
    try {
      const tasksRef = collection(this.db, 'c3_tasks');
      const q = query(
        tasksRef,
        where('incidentId', '==', incidentId),
        where('orgId', '==', orgId),
        where('status', '!=', 'completed')
      );

      const snapshot = await getDocs(q);
      const activeTasks = snapshot.docs.map(doc => doc.data());

      const workload = {};
      
      // Initialize workload for all roles
      Object.keys(this.roleCapabilities).forEach(role => {
        workload[role] = {
          activeTasks: 0,
          criticalTasks: 0,
          overdueTasks: 0,
          capacity: this.roleCapabilities[role].capacity,
          utilizationRate: 0,
          tasks: []
        };
      });

      // Calculate actual workload
      activeTasks.forEach(task => {
        const role = task.assignedRole;
        if (workload[role]) {
          workload[role].activeTasks++;
          workload[role].tasks.push(task);
          
          if (task.priority === 'critical') {
            workload[role].criticalTasks++;
          }
          
          // Check if overdue
          if (task.dueDate) {
            const dueDate = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
            if (dueDate < new Date()) {
              workload[role].overdueTasks++;
            }
          }
        }
      });

      // Calculate utilization rates
      Object.keys(workload).forEach(role => {
        workload[role].utilizationRate = workload[role].activeTasks / workload[role].capacity;
      });

      return workload;
      
    } catch (error) {
      logHelpfulError(error, 'Smart Task Service - getCurrentWorkload');
      
      // If it's an index error, provide helpful information
      if (isIndexError(error)) {
        // Return empty workload structure as fallback
        const fallbackWorkload = {};
        Object.keys(this.roleCapabilities).forEach(role => {
          fallbackWorkload[role] = {
            activeTasks: 0,
            criticalTasks: 0,
            overdueTasks: 0,
            capacity: this.roleCapabilities[role].capacity,
            utilizationRate: 0,
            tasks: []
          };
        });
        return fallbackWorkload;
      }
      
      return {};
    }
  }

  /**
   * Analyze task requirements to determine needed skills
   */
  analyzeTaskRequirements(task) {
    const taskName = (task.name || '').toLowerCase();
    const taskDescription = (task.description || '').toLowerCase();
    const taskText = `${taskName} ${taskDescription}`;

    const requirements = {
      skills: [],
      priority: task.priority || 'standard',
      urgency: this.determineUrgency(task),
      complexity: this.determineComplexity(task)
    };

    // Skill detection based on keywords
    const skillKeywords = {
      leadership: ['lead', 'coordinate', 'manage', 'oversee', 'direct', 'command'],
      safety: ['safety', 'evacuation', 'secure', 'protect', 'emergency'],
      security: ['security', 'threat', 'access', 'surveillance', 'guard'],
      media: ['media', 'press', 'public', 'statement', 'announcement'],
      stakeholder_comms: ['stakeholder', 'communicate', 'notify', 'update', 'inform'],
      legal: ['legal', 'compliance', 'regulatory', 'law', 'contract', 'liability'],
      hr: ['employee', 'staff', 'personnel', 'welfare', 'hr'],
      finance: ['cost', 'budget', 'financial', 'insurance', 'expense'],
      technology: ['system', 'it', 'tech', 'data', 'cyber', 'network'],
      operations: ['operations', 'logistics', 'facility', 'supply', 'process'],
      external_relations: ['government', 'authority', 'regulator', 'external']
    };

    Object.entries(skillKeywords).forEach(([skill, keywords]) => {
      if (keywords.some(keyword => taskText.includes(keyword))) {
        requirements.skills.push(skill);
      }
    });

    // If no specific skills detected, assign based on task type
    if (requirements.skills.length === 0) {
      requirements.skills = ['leadership']; // Default to leadership
    }

    return requirements;
  }

  /**
   * Score each role for a specific task
   */
  scoreRolesForTask(taskRequirements, currentWorkload) {
    const roleScores = [];

    Object.entries(this.roleCapabilities).forEach(([role, capabilities]) => {
      let score = 0;
      let reasoning = [];

      // Skill match score (0-40 points)
      const skillMatches = taskRequirements.skills.filter(skill => 
        capabilities.skills.includes(skill)
      ).length;
      const skillScore = Math.min(40, skillMatches * 20);
      score += skillScore;
      
      if (skillMatches > 0) {
        reasoning.push(`${skillMatches} skill match(es)`);
      }

      // Workload score (0-30 points) - lower workload = higher score
      const workload = currentWorkload[role] || { utilizationRate: 0 };
      const workloadScore = Math.max(0, 30 - (workload.utilizationRate * 30));
      score += workloadScore;
      
      if (workload.utilizationRate < 0.5) {
        reasoning.push('low workload');
      } else if (workload.utilizationRate > 0.8) {
        reasoning.push('high workload');
        score -= 10; // penalty for overload
      }

      // Priority match score (0-20 points)
      if (taskRequirements.priority === 'critical' && capabilities.priority <= 3) {
        score += 20;
        reasoning.push('suitable for critical tasks');
      } else if (taskRequirements.priority === 'important' && capabilities.priority <= 6) {
        score += 15;
        reasoning.push('suitable for important tasks');
      } else {
        score += 10;
      }

      // Availability penalty
      if (workload.overdueTasks > 0) {
        score -= 15;
        reasoning.push(`${workload.overdueTasks} overdue task(s)`);
      }

      roleScores.push({
        role,
        score: Math.max(0, score),
        reasoning: reasoning.join(', '),
        workload: workload.utilizationRate,
        skillMatches: skillMatches
      });
    });

    // Sort by score (highest first)
    return roleScores.sort((a, b) => b.score - a.score);
  }

  /**
   * Get the best assignment from scored roles
   */
  getBestAssignment(roleScores) {
    if (roleScores.length === 0) {
      return {
        role: 'cmt_leader',
        score: 0.5,
        reasoning: 'Default assignment'
      };
    }

    const best = roleScores[0];
    return {
      role: best.role,
      score: best.score / 100, // Convert to 0-1 scale
      reasoning: best.reasoning
    };
  }

  /**
   * Calculate workload impact of assignment
   */
  calculateWorkloadImpact(role, currentWorkload) {
    const workload = currentWorkload[role];
    if (!workload) return 'unknown';

    const newUtilization = (workload.activeTasks + 1) / workload.capacity;
    
    if (newUtilization <= 0.6) return 'low';
    if (newUtilization <= 0.8) return 'moderate';
    if (newUtilization <= 1.0) return 'high';
    return 'overloaded';
  }

  /**
   * Determine task urgency
   */
  determineUrgency(task) {
    if (!task.dueDate) return 'low';
    
    const dueDate = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
    const now = new Date();
    const hoursUntilDue = (dueDate - now) / (1000 * 60 * 60);
    
    if (hoursUntilDue <= 1) return 'immediate';
    if (hoursUntilDue <= 4) return 'urgent';
    if (hoursUntilDue <= 24) return 'high';
    return 'normal';
  }

  /**
   * Determine task complexity
   */
  determineComplexity(task) {
    const description = task.description || '';
    const estimatedDuration = task.estimatedDurationMinutes || 30;
    
    // Simple heuristics for complexity
    if (estimatedDuration > 120 || description.length > 200) return 'high';
    if (estimatedDuration > 60 || description.length > 100) return 'medium';
    return 'low';
  }

  /**
   * Suggest workload rebalancing
   */
  async suggestWorkloadRebalancing(incidentId, orgId) {
    try {
      const currentWorkload = await this.getCurrentWorkload(incidentId, orgId);
      const suggestions = [];

      // Find overloaded and underloaded roles
      const overloaded = [];
      const underloaded = [];

      Object.entries(currentWorkload).forEach(([role, workload]) => {
        if (workload.utilizationRate > 0.8) {
          overloaded.push({ role, workload });
        } else if (workload.utilizationRate < 0.4) {
          underloaded.push({ role, workload });
        }
      });

      // Generate rebalancing suggestions
      overloaded.forEach(({ role: overloadedRole, workload: overloadedWorkload }) => {
        const movableTasks = overloadedWorkload.tasks.filter(task => 
          task.priority !== 'critical' && task.status === 'not_started'
        );

        movableTasks.slice(0, 2).forEach(task => {
          // Find best alternative assignment
          const taskRequirements = this.analyzeTaskRequirements(task);
          const roleScores = this.scoreRolesForTask(taskRequirements, currentWorkload);
          
          // Find suitable underloaded role
          const suitableRole = roleScores.find(score => 
            underloaded.some(ul => ul.role === score.role) && score.score > 30
          );

          if (suitableRole) {
            suggestions.push({
              type: 'rebalance',
              taskId: task.id,
              taskName: task.name,
              fromRole: overloadedRole,
              toRole: suitableRole.role,
              reason: `Rebalance workload: ${overloadedRole} (${Math.round(overloadedWorkload.utilizationRate * 100)}% utilized)`,
              confidence: suitableRole.score / 100
            });
          }
        });
      });

      return suggestions;
      
    } catch (error) {
      console.error('Error suggesting workload rebalancing:', error);
      return [];
    }
  }

  /**
   * Create smart tasks based on incident analysis
   */
  async createSmartTasks(incidentId, orgId) {
    try {
      // Get incident details
      const incidentRef = doc(this.db, 'c3_incidents', incidentId);
      const incidentDoc = await getDoc(incidentRef);
      
      if (!incidentDoc.exists()) {
        throw new Error('Incident not found');
      }

      const incident = incidentDoc.data();
      const crisisType = incident.crisisType;

      // Get existing tasks
      const tasksRef = collection(this.db, 'c3_tasks');
      const q = query(
        tasksRef,
        where('incidentId', '==', incidentId),
        where('orgId', '==', orgId)
      );
      
      const snapshot = await getDocs(q);
      const existingTasks = snapshot.docs.map(doc => doc.data());

      // Generate crisis-specific tasks
      const generatedTasks = await generateCrisisTasks(crisisType, incident, orgId, this.db);
      
      // Filter out tasks that already exist
      const existingTaskNames = existingTasks.map(t => t.name.toLowerCase());
      const newTasks = generatedTasks.filter(task => 
        !existingTaskNames.some(existing => 
          existing.includes(task.name.toLowerCase()) || 
          task.name.toLowerCase().includes(existing)
        )
      );

      // Apply smart assignment to new tasks
      const smartTasks = [];
      for (const task of newTasks) {
        const assignment = await this.suggestTaskAssignment(task, incidentId, orgId);
        
        smartTasks.push({
          ...task,
          assignedRole: assignment.recommendedRole,
          assignmentConfidence: assignment.confidence,
          assignmentReasoning: assignment.reasoning,
          smartAssigned: true,
          createdBy: 'smart_automation',
          source: 'smart_task_creation'
        });
      }

      return smartTasks;
      
    } catch (error) {
      console.error('Error creating smart tasks:', error);
      return [];
    }
  }

  /**
   * Analyze team performance and suggest optimizations
   */
  async analyzeTeamPerformance(incidentId, orgId) {
    try {
      const currentWorkload = await this.getCurrentWorkload(incidentId, orgId);
      const analysis = {
        overallUtilization: 0,
        bottlenecks: [],
        underutilized: [],
        recommendations: []
      };

      let totalUtilization = 0;
      let activeRoles = 0;

      Object.entries(currentWorkload).forEach(([role, workload]) => {
        if (workload.activeTasks > 0) {
          activeRoles++;
          totalUtilization += workload.utilizationRate;
        }

        // Identify bottlenecks
        if (workload.utilizationRate > 0.9) {
          analysis.bottlenecks.push({
            role,
            utilization: workload.utilizationRate,
            activeTasks: workload.activeTasks,
            overdueTasks: workload.overdueTasks
          });
        }

        // Identify underutilized roles
        if (workload.utilizationRate < 0.3 && workload.activeTasks > 0) {
          analysis.underutilized.push({
            role,
            utilization: workload.utilizationRate,
            activeTasks: workload.activeTasks
          });
        }
      });

      analysis.overallUtilization = activeRoles > 0 ? totalUtilization / activeRoles : 0;

      // Generate recommendations
      if (analysis.bottlenecks.length > 0) {
        analysis.recommendations.push({
          type: 'rebalance_workload',
          priority: 'high',
          message: `${analysis.bottlenecks.length} role(s) are overloaded. Consider redistributing tasks.`
        });
      }

      if (analysis.underutilized.length > 0) {
        analysis.recommendations.push({
          type: 'utilize_capacity',
          priority: 'medium',
          message: `${analysis.underutilized.length} role(s) have available capacity for additional tasks.`
        });
      }

      if (analysis.overallUtilization < 0.4) {
        analysis.recommendations.push({
          type: 'increase_activity',
          priority: 'medium',
          message: 'Overall team utilization is low. Consider creating additional tasks or reviewing incident scope.'
        });
      }

      return analysis;
      
    } catch (error) {
      console.error('Error analyzing team performance:', error);
      return {
        overallUtilization: 0,
        bottlenecks: [],
        underutilized: [],
        recommendations: []
      };
    }
  }

  /**
   * Get role capabilities information
   */
  getRoleCapabilities() {
    return this.roleCapabilities;
  }

  /**
   * Update role capabilities (for customization)
   */
  updateRoleCapabilities(role, updates) {
    if (this.roleCapabilities[role]) {
      this.roleCapabilities[role] = {
        ...this.roleCapabilities[role],
        ...updates
      };
    }
  }
}

export default SmartTaskService; 